// src/components/character_creation/CharacterCreation.jsx
// Полностью на Redux (characterDraft). Локального useState для characterData нет.
// Онтологии по-прежнему грузятся через Gateway (они не часть черновика).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container
} from '@mui/material';

import RaceSelection from './RaceSelection';
import TemplateSelection from './TemplateSelection';
import NameAndBackground from './NameAndBackground';
import SkillsSelection from './SkillsSelection';
import FinalizeCreation from './FinalizeCreation';

import Gateway from '../../api/gateway';
import { useCharacterDraft } from '../../hooks/useCharacterDraft';

const steps = [
  'Выбор расы',
  'Шаблон и темперамент',
  'Имя и предыстория',
  'Навыки и способности',
  'Завершение создания',
];

const CharacterCreation = ({ onCharacterCreated }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const {
    draft,
    activeStep,
    stepErrors,
    validationErrors,
    submitError,
    beginCreate,
    reset,
    next,
    back,
    goTo,
    selectRace,
    selectTemplate,
    setSkillsList,
    save,
    clearSubmit,
  } = useCharacterDraft();

  const [ontologyData, setOntologyData] = useState(null);
  const [ontologyLoading, setOntologyLoading] = useState(false);
  const [ontologyError, setOntologyError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Инициализация черновика при монтировании и загрузка онтологий
  useEffect(() => {
    beginCreate();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    let cancelled = false;
    const loadOntologyData = async () => {
      setOntologyLoading(true);
      setOntologyError('');
      try {
        const data = await Gateway.api.ontology.getAllForCharacterCreation();
        if (!cancelled) setOntologyData(data);
      } catch (err) {
        if (!cancelled) {
          setOntologyError(
            `Не удалось загрузить данные: ${Gateway.utils.getErrorMessage(err)}`
          );
        }
      } finally {
        if (!cancelled) setOntologyLoading(false);
      }
    };
    loadOntologyData();

    return () => {
      cancelled = true;
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const handleCreateCharacter = async () => {
    if (!user?.id) {
      clearSubmit();
      return;
    }
    setSubmitting(true);
    try {
      const created = await save(user.id);
      if (created && onCharacterCreated) {
        onCharacterCreated(created);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (ontologyLoading && !ontologyData) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Загрузка данных...</Typography>
        </Box>
      );
    }
    if (ontologyError && !ontologyData) {
      return <Alert severity="error" sx={{ my: 2 }}>{ontologyError}</Alert>;
    }
    if (!ontologyData) return null;

    switch (activeStep) {
      case 0:
        return (
          <RaceSelection
            races={ontologyData.races || []}
            selectedRace={draft.race}
            onRaceSelect={selectRace}
          />
        );
      case 1:
        return (
          <TemplateSelection
            templates={ontologyData.character_templates || []}
            selectedRace={draft.race}
            selectedTemplate={draft.character_template}
            selectedTemperament={draft.temperament}
            onTemplateSelect={selectTemplate}
          />
        );
      case 2:
        return (
          <NameAndBackground
            race={draft.race}
            characterTemplate={draft.character_template}
            temperament={draft.temperament}
          />
        );
      case 3:
        return (
          <SkillsSelection
            skills={ontologyData.skills || []}
            race={draft.race}
            characterTemplate={draft.character_template}
            selectedSkills={draft.skills}
            onSkillsUpdate={setSkillsList}
          />
        );
      case 4:
        return (
          <FinalizeCreation
            onCreate={handleCreateCharacter}
            onEditStep={goTo}
            loading={submitting}
          />
        );
      default:
        return <Typography>Неизвестный шаг</Typography>;
    }
  };

  const currentStepKey = ['race', 'template', 'identity', 'skills', 'finalize'][activeStep];
  const localStepError = stepErrors[currentStepKey];
  const displayError =
    localStepError ||
    (submitError) ||
    (validationErrors.length > 0 ? validationErrors.join(', ') : '');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          🚀 Создание персонажа
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph align="center">
          Создайте уникального персонажа для вселенной Stellar Frontier.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearSubmit}>
            {displayError}
          </Alert>
        )}

        <Box sx={{ my: 3 }}>{renderStepContent()}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0 || submitting} onClick={back} variant="outlined">
            Назад
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateCharacter}
                disabled={submitting}
              >
                {submitting ? 'Создание...' : 'Создать персонажа'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={next}
                disabled={submitting}
              >
                Далее
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CharacterCreation;
